/**
 * The WebRTC handshake, driven off the relay: offer, answer, trickled ICE, and
 * one DataChannel that becomes a `NetTransport`.
 *
 * Role is decided by arrival order at the relay, not by the UI — whoever got
 * the room first offers. That removes the whole class of "both sides offered"
 * glare handling, at the cost of nothing: the relay already has to tell each
 * socket which seat it took.
 *
 * ICE candidates are queued until a remote description exists. Trickle ICE
 * routinely delivers a candidate before the answer it belongs to, and
 * `addIceCandidate` throws in that window; buffering is the standard fix and
 * is three lines.
 */

import type { NetTransport } from "./transport.ts";
import type { RtcSignal, SignalRole } from "./signaling.ts";
import {
  DEAD_PEER_STATES,
  DUEL_CHANNEL_LABEL,
  type DataChannelLike,
  type IceCandidateInit,
  type PeerConnectionLike,
} from "./peer-connection.ts";

export interface RtcSessionHooks {
  /** Hand this to the relay verbatim. */
  readonly onSignal: (signal: RtcSignal) => void;
  /** Fired once, when the channel opens and frames can flow. */
  readonly onOpen: (transport: NetTransport) => void;
  readonly onClose: (reason: string) => void;
}

export interface RtcSession {
  /** Host: opens the channel and offers. Guest: nothing — it waits for the offer. */
  start(): Promise<void>;
  /** Feeds one signal that arrived through the relay. */
  accept(signal: RtcSignal): Promise<void>;
  close(): void;
}

function channelTransport(channel: DataChannelLike): NetTransport {
  const backlog: string[] = [];
  let handler: ((frame: string) => void) | null = null;
  channel.onmessage = (frame) => {
    if (handler) handler(frame);
    else backlog.push(frame);
  };
  return {
    send: (frame) => channel.send(frame),
    receive: (next) => {
      handler = next;
      for (const frame of backlog.splice(0)) next(frame);
    },
    close: () => channel.close(),
  };
}

class Session implements RtcSession {
  private readonly pending: IceCandidateInit[] = [];
  private remoteReady = false;
  private opened = false;

  constructor(
    private readonly role: SignalRole,
    private readonly peer: PeerConnectionLike,
    private readonly hooks: RtcSessionHooks,
  ) {
    peer.onicecandidate = (candidate) => this.emitCandidate(candidate);
    peer.ondatachannel = (channel) => this.attach(channel);
    peer.onstatechange = (state) => {
      if (DEAD_PEER_STATES.includes(state)) hooks.onClose(`peer connection ${state}`);
    };
  }

  private emitCandidate(candidate: IceCandidateInit | null): void {
    if (!candidate || candidate.candidate.length === 0) return;
    this.hooks.onSignal({ t: "ice", candidate: candidate.candidate, mid: candidate.sdpMid, index: candidate.sdpMLineIndex });
  }

  private attach(channel: DataChannelLike): void {
    const transport = channelTransport(channel);
    channel.onclose = () => this.hooks.onClose("data channel closed");
    channel.onopen = () => {
      if (this.opened) return;
      this.opened = true;
      this.hooks.onOpen(transport);
    };
  }

  private async drainCandidates(): Promise<void> {
    this.remoteReady = true;
    for (const candidate of this.pending.splice(0)) await this.peer.addIceCandidate(candidate);
  }

  async start(): Promise<void> {
    if (this.role !== "host") return;
    this.attach(this.peer.createDataChannel(DUEL_CHANNEL_LABEL));
    const offer = await this.peer.createOffer();
    await this.peer.setLocalDescription(offer);
    this.hooks.onSignal({ t: "offer", sdp: offer.sdp });
  }

  async accept(signal: RtcSignal): Promise<void> {
    if (signal.t === "ice") {
      const candidate: IceCandidateInit = { candidate: signal.candidate, sdpMid: signal.mid, sdpMLineIndex: signal.index };
      if (!this.remoteReady) this.pending.push(candidate);
      else await this.peer.addIceCandidate(candidate);
      return;
    }
    if (signal.t === "answer") {
      await this.peer.setRemoteDescription({ type: "answer", sdp: signal.sdp });
      await this.drainCandidates();
      return;
    }
    await this.peer.setRemoteDescription({ type: "offer", sdp: signal.sdp });
    const answer = await this.peer.createAnswer();
    await this.peer.setLocalDescription(answer);
    this.hooks.onSignal({ t: "answer", sdp: answer.sdp });
    await this.drainCandidates();
  }

  close(): void {
    this.peer.close();
  }
}

export function createRtcSession(role: SignalRole, peer: PeerConnectionLike, hooks: RtcSessionHooks): RtcSession {
  return new Session(role, peer, hooks);
}
